import React from 'react';
import { isEmpty } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { PivotFieldsAggridT } from 'business/definitions';
import BudgetPivotGrid from 'routes/BudgetModule/components/shared/BudgetChart/BudgetPivotGrid/BudgetPivotGrid';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { stringifyJsonFromResponseData } from 'helpers/formatHelper';
import { queryToSearch } from 'routes/Explore/components/Sentence/helpers';
import { useParams } from 'react-router-dom';
import HttpErrorWrapper from '../../ExpenseIncome/components/HttpErrorWrapper';
import { PROJECTS_PIVOT_GRID_CACHE_KEY } from './ProjectsPivotGrid';
import { triggerAnalyseTCDEvent } from '../helpers/trackersHelpers';
import { useDebounce } from 'hooks/helpers';
import { useCustomGetData } from '../../ExpenseIncome/hooks/getDataWithCustomEndpoint';
import { FilterChangedEvent, FilterModel } from 'ag-grid-community';
import { useFullScreen } from 'contexts/FullScreenContext';

const DEBOUNCE_TIME = 2000;

type Props = {
    filters: QueryFilterT[];
    onSettingsUpdate: (arg1: PivotFieldsAggridT) => void;
    updateGridFilters?: (event: FilterChangedEvent) => void;
    gridFilters?: FilterModel;
    selectedYear: number;
    budgetProposal: BudgetProposalT;
    resourceName?: string;
    setExporterToExcel: null | ((arg1: FlowAnyFunction) => void);
    settings: PivotFieldsAggridT | undefined;
    isLoading?: boolean;
};

const AnalysePivotGrid = ({
    filters,
    onSettingsUpdate,
    updateGridFilters,
    gridFilters,
    selectedYear,
    budgetProposal,
    resourceName = undefined,
    setExporterToExcel: setExporterToExcelProp,
    settings,
    isLoading: isLoadingProp = false,
}: Props): React.ReactElement => {
    const queryClient = useQueryClient();

    const { resourceSoftwareId = '' } = useParams();

    const { isFullScreen } = useFullScreen();

    const queryKey = [
        budgetProposal.id,
        resourceSoftwareId,
        JSON.stringify(filters),
    ];

    const endpointUrl = `budget_proposals/get_analysis_data?${queryToSearch({
        filters: filters,
        metrics: [],
        dimensions: [],
    })}&budget_by=${budgetProposal.by}`;

    const {
        data: budgetLineResponse,
        isFetching: isDataFetching,
        error,
        isError,
    } = useCustomGetData(endpointUrl, queryKey, {
        enabled: !isLoadingProp,
    });

    const data =
        budgetLineResponse?.data.map(stringifyJsonFromResponseData) || [];
    const columns = budgetLineResponse?.columns || [];

    const isLoading =
        (isEmpty(data) && isEmpty(columns)) || isDataFetching || isLoadingProp;

    // we use a useCallback to make sure the debounced function stays the same,
    // if it is redefined, the debounce should not work
    const debouncedMutateSettings = useDebounce(
        onSettingsUpdate,
        DEBOUNCE_TIME,
    );

    const onInscriptionsModalClosed = () => {
        // we need to invalidate all the queries that are cached (current and all the previously made thru filtering)
        queryClient.invalidateQueries({
            queryKey: [PROJECTS_PIVOT_GRID_CACHE_KEY],
        });
        // and then we can refresh the active query that is also used in other components, namely ProjectPivotGrid
        queryClient.invalidateQueries({
            queryKey: queryKey,
        });
    };
    const onInscriptionsModalOpened = () => {
        triggerAnalyseTCDEvent(resourceName, budgetProposal, 'penModalOpened');
    };

    // This is a trick to inject the tracker logic in the export function
    // without changing the generic child component.
    // Since the state is a function and that setState accepts a function to set the state,
    // we are forced to use a function that returns a function as an argument.
    // We should avoid having functions as state.
    const setExporterToExcel = (getExporter: () => () => void) => {
        if (!setExporterToExcelProp) return;

        setExporterToExcelProp(() => () => {
            triggerAnalyseTCDEvent(
                resourceName,
                budgetProposal,
                'pivotGridExport',
            );
            getExporter()();
        });
    };

    return (
        <HttpErrorWrapper error={error} isError={isError}>
            <BudgetPivotGrid
                data={data}
                selectedYear={selectedYear}
                isLoading={isLoading}
                setExporterToExcel={setExporterToExcel}
                pivotFieldsAggrid={settings}
                updateGridFilters={updateGridFilters}
                gridFilters={gridFilters}
                onPivotFieldsAggridUpdate={debouncedMutateSettings}
                budgetProposal={budgetProposal}
                onInscriptionsModalOpened={onInscriptionsModalOpened}
                onInscriptionsModalClosed={onInscriptionsModalClosed}
                baseFilters={filters}
                pagination={!isFullScreen}
                sx={{
                    // Necessary (for now) so the grid can grow to its content
                    '& .ag-root-wrapper-body.ag-layout-normal': {
                        height: 'max-content',
                    },
                    /* Necessary (for now) so the grid without content still has room to display
                     * "No rows to show" without overlapping the header */
                    '& .ag-root.ag-layout-normal': {
                        minHeight: '10rem',
                    },
                    ...(isFullScreen && {
                        height: '75vh',
                        minHeight: '26rem',
                    }),
                }}
            />
        </HttpErrorWrapper>
    );
};

export default AnalysePivotGrid;
